type SchemaBreadcrumbItem =
  | string
  | {
      '@id': string
      name?: string
    }

interface SchemaBreadcrumb {
  '@type': 'ListItem'
  position: number
  name: string
  item: SchemaBreadcrumbItem
}

export default (opt?: {
  title?: string
  description?: string
  keywords?: string
  robots?: string
  image?: string
  author?: string
  canonical?: string
  schema?: {
    type?: string | string[]
    applicationCategory?: string
    applicationSubCategory?: string
    operatingSystem?: string
    title?: string
    breadcrumbs?: SchemaBreadcrumb[]
  }
}) => {
  const config = useRuntimeConfig()

  const { proxy: gtmProxy } = useScriptGoogleTagManager()

  useScriptEventPage(({ title, path }) => {
    gtmProxy.dataLayer.push({
      event: 'pageview',
      title,
      path
    })
  })

  const route = useRoute()
  const defaultSchemaBreadcrumbs: SchemaBreadcrumb[] = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Beranda',
      item: config.public.url
    }
  ]

  const normalizeSchemaBreadcrumbs = (
    breadcrumbs: Array<SchemaBreadcrumb | null | undefined>
  ) => {
    const fallbackItemId = config.public.url + route.fullPath

    const isDefined = (
      value: SchemaBreadcrumb | null | undefined
    ): value is SchemaBreadcrumb => value != null

    return breadcrumbs.filter(isDefined).map((breadcrumb) => {
      const itemId =
        typeof breadcrumb.item === 'string'
          ? breadcrumb.item
          : breadcrumb.item?.['@id']

      return {
        ...breadcrumb,
        item: {
          '@id': itemId || fallbackItemId,
          name:
            typeof breadcrumb.item === 'string'
              ? breadcrumb.name
              : (breadcrumb.item?.name ?? breadcrumb.name)
        }
      }
    })
  }
  const defaultTitle =
    'Aplikasi Kirim Paket COD Terbaik #1 Indonesia | KiriminAja'

  const defaultDescription =
    'Aggregator logistik terlengkap &amp; termurah untuk kirim paket COD dan Non COD via seluruh jasa pengiriman, jasa kurir &amp; jasa ekspedisi. Daftar sekarang!'

  const defaultKeyword =
    'KiriminAja,kirim paket ,Multi expedisi ,kirim paket COD, Cara daftar COD ,COD tanpa marketplace, JNE Express, J&amp;T Express, Sicepat, Gratis Ongkir'

  useSeoMeta({
    title: opt?.title || defaultTitle,
    description: opt?.description || defaultDescription,
    ogTitle: opt?.title || defaultTitle,
    ogDescription: opt?.description || defaultDescription,
    ogImage: opt?.image || '/images/home-v3/share-card-home.png',
    ogUrl: opt?.canonical || config.public.url + route.fullPath,
    twitterTitle: opt?.title || defaultTitle,
    twitterDescription: opt?.description || defaultDescription,
    twitterImage: opt?.image || '/images/home-v3/share-card-home.png',
    twitterCard: 'summary_large_image',
    keywords: opt?.keywords || defaultKeyword,
    robots: opt?.robots || 'index, follow',
    publisher: 'KiriminAja',
    author: (opt?.author || 'IT Team') + ' - KiriminAja'
  })

  if (route.fullPath !== '/') {
    const pageTitle = opt?.schema?.title || opt?.title || 'Halaman'
    const pageUrl = config.public.url + route.fullPath

    const schemaBreadcrumbs: SchemaBreadcrumb[] = opt?.schema?.breadcrumbs
      ?.length
      ? opt.schema.breadcrumbs
      : [
          {
            '@type': 'ListItem',
            position: 2,
            name: pageTitle,
            item: pageUrl
          }
        ]

    useHead({
      script: [
        {
          key: 'schema-breadcrumb',
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: normalizeSchemaBreadcrumbs([
              ...defaultSchemaBreadcrumbs,
              ...schemaBreadcrumbs
            ])
          })
        },
        {
          key: 'schema-current-page',
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': opt?.schema?.type || 'WebPage',
            '@id': pageUrl + '#page',
            name: opt?.title || pageTitle,
            description: opt?.description || defaultDescription,
            headline: opt?.title || pageTitle,
            images: [opt?.image || '/images/home-v3/share-card-home.png'],
            url: pageUrl,
            applicationCategory: opt?.schema?.applicationCategory || undefined,
            applicationSubCategory:
              opt?.schema?.applicationSubCategory || undefined,
            operatingSystem: opt?.schema?.operatingSystem || undefined,
            inLanguage: 'id',
            author: opt?.author
              ? [
                  {
                    '@type': 'Person',
                    name: opt.author
                  }
                ]
              : undefined,
            publisher: {
              '@type': 'Organization',
              name: 'KiriminAja',
              url: config.public.url
            },
            isAccessibleForFree: true
          })
        }
      ]
    })
  }
  return {
    defaultSchemaBreadcrumbs
  }
}
